import { type ApiRequisition, EnumContainerActionRequest, EnumContainerOrderAction } from '@Model';
import { type BadgeVariations } from '@synqo/frontend-component-library';

/**
 * The single action a requisition carries. `material` is the loose-material order that existed
 * before containers; `swap` is named so the tiles can offer it, but no flow leads there yet.
 */
export type RequisitionAction = 'placement' | 'pickup' | 'emptying' | 'swap' | 'material';

/** The three actions that reach the server as a container action. */
export type ContainerAction = Extract<RequisitionAction, 'placement' | 'pickup' | 'emptying'>;

/** Whether an action is about containers at all, which is what decides the shape of step 3. */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export const isContainerAction = (action: RequisitionAction | undefined): action is ContainerAction =>
    action === 'placement' || picksFromStock(action);

/** Whether a stored container action is one half of an exchange, which only the server pairs. */
// @ai-generated 2026-10-08T21:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const isExchangeHalf = (row: { exchange_id?: string | undefined }): boolean => !!row.exchange_id;

/** What the server can be asked for on a container order. */
export type ContainerActionRequest = EnumContainerActionRequest;

/** What the server is asked for by an action: undefined for loose material and before a tile is chosen. */
// @ai-generated 2026-10-09T08:04:13+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function toActionRequest(action: RequisitionAction | undefined): ContainerActionRequest | undefined {
    switch (action) {
        case 'placement':
            return EnumContainerActionRequest.Place;
        case 'pickup':
            return EnumContainerActionRequest.Collect;
        case 'emptying':
            return EnumContainerActionRequest.Empty;
        case 'swap':
            return EnumContainerActionRequest.Exchange;
        default:
            return undefined;
    }
}

/** Whether the action is about containers at all: one of the three, or an exchange of one for another. */
// @ai-generated 2026-10-08T21:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const carriesContainers = (action: RequisitionAction | undefined): boolean =>
    toActionRequest(action) !== undefined;

/** Whether the action is about a container that already stands at the site, picked off its stock. */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export const picksFromStock = (action: RequisitionAction | undefined): boolean =>
    action === 'pickup' || action === 'emptying';

/** How a container action reaches the server. */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function toApiAction(action: ContainerAction): EnumContainerOrderAction {
    switch (action) {
        case 'placement':
            return EnumContainerOrderAction.Place;
        case 'pickup':
            return EnumContainerOrderAction.Collect;
        case 'emptying':
            return EnumContainerOrderAction.Empty;
    }
}

/**
 * The action a stored container action stands for.
 *
 * A row the server has not been told about yet carries `NotDefined`; there is no wizard action
 * behind that, so it reads as "nothing chosen".
 */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function fromApiAction(action: EnumContainerOrderAction): ContainerAction | undefined {
    switch (action) {
        case EnumContainerOrderAction.Place:
            return 'placement';
        case EnumContainerOrderAction.Collect:
            return 'pickup';
        case EnumContainerOrderAction.Empty:
            return 'emptying';
        default:
            return undefined;
    }
}

/** The label the stepper and the footer use for step 3, once an action is chosen. */
export function actionVerb(action: RequisitionAction | undefined): string {
    switch (action) {
        case 'placement':
            return 'Stellen';
        case 'pickup':
            return 'Abholen';
        case 'material':
            return 'Material';
        case 'emptying':
            return 'Leeren';
        case 'swap':
            return 'Tauschen';
        default:
            return 'Details';
    }
}

/**
 * The action as a sentence names it, e.g. "für die Abholung erfasst".
 *
 * The stepper's verb ("Abholen") is a label; a sentence about the order needs the noun, or it
 * reads like system speak.
 */
export function actionNoun(action: RequisitionAction | undefined): string {
    switch (action) {
        case 'placement':
            return 'die Aufstellung';
        case 'pickup':
            return 'die Abholung';
        case 'material':
            return 'das lose Material';
        case 'emptying':
            return 'die Leerung';
        case 'swap':
            return 'den Tausch';
        default:
            return 'diese Bestellung';
    }
}

/** What the tile of an action says, word for word — the title a sentence quotes it by. */
export function actionTileTitle(action: RequisitionAction | undefined): string {
    switch (action) {
        case 'placement':
            return 'Container stellen';
        case 'pickup':
            return 'Container abholen lassen';
        case 'material':
            return 'Loses Material';
        case 'emptying':
            return 'Container leeren lassen';
        case 'swap':
            return 'Container tauschen';
        default:
            return '';
    }
}

/**
 * The chip the summary and the order lists show for the action of an order.
 *
 * One implementation for both places: a second one would drift the moment a wording changes.
 */
export function actionChip(action: RequisitionAction): { label: string; variant: BadgeVariations } {
    switch (action) {
        case 'placement':
            return { label: 'Aufstellung', variant: 'info' };
        case 'pickup':
            return { label: 'Abholung', variant: 'default' };
        case 'material':
            return { label: 'Material', variant: 'warning' };
        case 'emptying':
            return { label: 'Leerung', variant: 'accent' };
        case 'swap':
            return { label: 'Tausch', variant: 'primary' };
    }
}

/** The colour of a stored container action's badge, the same as the chip of its action. */
// @ai-generated 2026-09-29T07:49:58+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function getVariantByActionType(action: EnumContainerOrderAction): BadgeVariations {
    const known = fromApiAction(action);

    return known ? actionChip(known).variant : 'default';
}

/**
 * Whether step 3 counts a selection or a list of positions.
 *
 * Picking off the stock list is a selection; without a recorded container the same step collects
 * described positions like a placement does, and calling those "gewählt" would name something
 * the customer never picked.
 */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export const countsSelection = (action: RequisitionAction | undefined, hasSelectableStock: boolean): boolean =>
    picksFromStock(action) && hasSelectableStock;

/**
 * What a requisition is about, read off its container action.
 *
 * There is no action field on the requisition: an order carrying a container action is about that
 * container, and one carrying none is a loose-material order. A row the server has not been told
 * about yet reads as loose material too, which is what the wizard shows for it.
 */
export function requisitionActionOf(requisition: Pick<ApiRequisition, 'container_actions'>): RequisitionAction {
    const actions = requisition.container_actions ?? [];
    if (actions.some(isExchangeHalf)) {
        return 'swap';
    }

    const stored = actions[0];

    return (stored && fromApiAction(stored.action)) ?? 'material';
}
